import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { Player } from "@lottiefiles/react-lottie-player";
import React, { FunctionComponent, MouseEvent, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { ReactComponent as IconArrowDown } from "../../../../icons/16x16/chevron-down.svg";
import {
  getDatesListFromDate,
  monthArray,
  MonthsList,
} from "../../../../utils/customization/calendar";
import ButtonSmily from "../../../atoms/ButtonSmily";
import ActionsModalSmily from "../../modals/ActionsModalSmily";
import autumn from "./files/autumn.json";
import summer from "./files/summer.json";
import winter from "./files/winter.json";
import oblong from "./files/oblong.json";
import spring from "./files/spring.json";
import "./style.scss";

interface Props {
  onConfirm: (date: string) => void;
}

const StartingDateChoice: FunctionComponent<Props> = ({ onConfirm }) => {
  const intl = useIntl();
  const backAnimationRef = useRef<Player>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropDownRef = useRef<HTMLUListElement>(null);

  const startDate = new Date();
  const dateString = startDate.toDateString();

  const twoYearsDates = getDatesListFromDate(dateString, 24, false);

  const [selectedDate, setSelectedDate] = useState<MonthsList>(twoYearsDates[0]);
  const [currentSeason, setCurrentSeason] = useState<
    "winter" | "spring" | "summer" | "autumn" | null
  >(getSeason(twoYearsDates[0].month));

  const monthSuggestionsCheck: string[] = [];
  const todayMonth = startDate.getMonth();
  if (todayMonth > 0) {
    monthSuggestionsCheck.push("january");
  }
  if (todayMonth < 8) {
    monthSuggestionsCheck.push("september");
  }

  const monthSuggestions = twoYearsDates
    .filter((monthDetails) => monthSuggestionsCheck.includes(monthDetails.month))
    .slice(0, monthSuggestionsCheck.length);

  const handleSelectMonth = (selectedMonth: MonthsList) => {
    setSelectedDate(selectedMonth);
    const newSeason = getSeason(selectedMonth.month);

    if (newSeason != currentSeason) {
      setCurrentSeason(null);
      setTimeout(() => setCurrentSeason(getSeason(selectedMonth.month)), 1200);

      if (backAnimationRef.current) {
        backAnimationRef.current.play();
      }
    }
  };

  const seasons = [
    {
      isActive: () => currentSeason === "winter",
      key: "winter",
      src: winter,
    },
    {
      isActive: () => currentSeason === "spring",
      key: "spring",
      src: spring,
    },
    {
      isActive: () => currentSeason === "summer",
      key: "summer",
      src: summer,
    },
    {
      isActive: () => currentSeason === "autumn",
      key: "autumn",
      src: autumn,
    },
  ];

  const handleMouseDownDropDown = (e: MouseEvent<HTMLDivElement>) => {
    const rect = e.target && (e.target as HTMLElement).getBoundingClientRect();

    if (dropDownRef?.current && rect) {
      const dropDownContainer = containerRef?.current && containerRef.current.parentElement;
      const containerRefValues = dropDownContainer && dropDownContainer.getBoundingClientRect();
      const refHeight = containerRefValues ? containerRefValues.bottom : window.innerHeight;
      const maxHeight = refHeight - rect.bottom;
      dropDownRef.current.style.maxHeight = `${maxHeight}px`;
    }
  };

  return (
    <ActionsModalSmily
      className="StartingDateChoice-modale"
      confirmLabel={intl.formatMessage({ id: "kustom_LottieCarousel_button_next" })}
      dismissOnClickOutside={false}
      display={true}
      onDismiss={() => onConfirm(getDateValue(selectedDate))}
      showCloseButton={false}
      title={intl.formatMessage({ id: "kustom_StartingDateChoice_title" })}
    >
      <div className="StartingDateChoice" ref={containerRef}>
        <div className="StartingDateChoice-seasons">
          <div className="StartingDateChoice-seasons-background">
            <Player
              autoplay={false}
              loop={false}
              ref={backAnimationRef}
              renderer="svg"
              src={oblong}
              style={{
                height: "118px",
                width: "154px",
              }}
            />
          </div>

          <AnimatePresence>
            {seasons.map(
              (season) =>
                season.isActive() && (
                  <motion.div
                    animate={{ scale: 1, zIndex: 2 }}
                    className="StartingDateChoice-seasons-item"
                    exit={{ scale: 0.3, transition: { delay: 0.4 } }}
                    initial={{ scale: 0.3, zIndex: 1 }}
                    key={season.key}
                    transition={{ duration: 0.12 }}
                  >
                    <Player
                      autoplay={true}
                      loop={true}
                      renderer="svg"
                      src={season.src}
                      style={{
                        height: `110px`,
                        width: `144px`,
                      }}
                    />
                  </motion.div>
                )
            )}
          </AnimatePresence>
        </div>

        <h1 className="StartingDateChoice-title">
          {intl.formatMessage({ id: "kustom_StartingDateChoice_choose" })}
        </h1>

        <div className="StartingDateChoice-dropDown">
          <div
            className="StartingDateChoice-dropDown-button"
            onMouseDown={handleMouseDownDropDown}
            tabIndex={1}
          >
            {getReadableDate(selectedDate, intl.locale)} <IconArrowDown />
          </div>

          <div className="StartingDateChoice-dropDown-list">
            <ul ref={dropDownRef}>
              {twoYearsDates.map((monthValues) => {
                const currentDate = getReadableDate(monthValues, intl.locale);
                return (
                  <li
                    className={classNames("StartingDateChoice-dropDown-list-item", {
                      "StartingDateChoice-dropDown-list-item-selected":
                        getReadableDate(selectedDate, intl.locale) === currentDate,
                    })}
                    key={currentDate}
                    onMouseDown={() => handleSelectMonth(monthValues)}
                  >
                    {currentDate}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="StartingDateChoice-title">
          {intl.formatMessage({ id: "kustom_StartingDateChoice_alternative" })}
        </div>
        <div className="StartingDateChoice-suggestions">
          {monthSuggestions.map((monthValues) => {
            const currentDate = getReadableDate(monthValues, intl.locale);
            const dateValue = getDateValue(monthValues);
            return (
              <ButtonSmily
                key={dateValue}
                onClick={() => onConfirm(dateValue)}
                size="m"
                type="tertiary"
              >
                {currentDate}
              </ButtonSmily>
            );
          })}
        </div>
      </div>
    </ActionsModalSmily>
  );
};

function getReadableDate(monthValues: MonthsList, lang: string): string {
  const date = new Date();
  date.setMonth(monthArray.indexOf(monthValues.month));
  const month = date.toLocaleDateString(lang, { month: "long" });
  return `${month} ${monthValues.year}`;
}

function getDateValue(monthValues: MonthsList): string {
  const monthNumber = monthArray.indexOf(monthValues.month) + 1;
  return `${monthValues.year}-${monthNumber > 9 ? "" : "0"}${monthNumber}`;
}

function getSeason(month: string) {
  if (["january", "february", "march"].includes(month)) {
    return "winter";
  }
  if (["april", "may", "june"].includes(month)) {
    return "spring";
  }
  if (["july", "august", "september"].includes(month)) {
    return "summer";
  }
  if (["october", "november", "december"].includes(month)) {
    return "autumn";
  }
  return null;
}

export default StartingDateChoice;
